import { inject, Injectable } from '@angular/core';
import { CrudService } from '@shared/modules/crud/crud.service';
import { FirebaseLibService } from '@shared/modules/firebase/firebase-lib.service';
import { CocheraConsorcista } from '@pages/consorcios/cocheras.service';
import { Consorcio } from '@pages/consorcios/consorcios.config';
import { Consorcista } from '@pages/consorcios/consorcistas.service';
import { firstValueFrom } from 'rxjs';
import { KEY_REQUEST_TOKENS, KEY_REQUESTS_SUBCOLLECTION, KeyRequest, KeyRequestToken } from './key-requests.config';

/*
Acceso a Firestore del pedido de copia de llave (AF298): tokens de los links, pedidos y los datos de consorcistas
y consorcios que usa el formulario. La lógica (validar, armar el pedido) está en KeyRequestsManagerService.
⚠ El formulario público lee y escribe desde el navegador, sin sesión (como el registro de consorcistas): depende de las
reglas de Firestore. Propuesta pendiente: pasar esto a una function (ver errores_pendientes).
*/
@Injectable({
    providedIn: 'root',
})
export class KeyRequestsService {
    private crudService = inject(CrudService);
    private firebaseService = inject(FirebaseLibService);

    async getToken(cod: string): Promise<KeyRequestToken | undefined> {
        this.crudService.setModel(KEY_REQUEST_TOKENS);
        const token = await firstValueFrom(this.crudService.findFirst<any>(cod));
        if (!token) {
            return undefined;
        }
        return { ...token, createdAt: toDate(token.createdAt), expiresAt: toDate(token.expiresAt) } as KeyRequestToken;
    }

    getTokenPath(cod: string): string {
        return KEY_REQUEST_TOKENS + '/' + cod;
    }

    async getConsorcista(consorcioId: string, consorcistaId: string): Promise<Consorcista | undefined> {
        this.crudService.setModel('consorcios/' + consorcioId + '/consorcistas');
        const consorcista = await firstValueFrom(this.crudService.findFirst<any>(consorcistaId));
        return consorcista ? { ...consorcista, uid: consorcistaId, consorcioId } : undefined;
    }

    getConsorcistaPath(consorcioId: string, consorcistaId: string): string {
        return 'consorcios/' + consorcioId + '/consorcistas/' + consorcistaId;
    }

    async getConsorcio(consorcioId: string): Promise<Consorcio | undefined> {
        this.crudService.setModel('consorcios');
        const consorcio = await firstValueFrom(this.crudService.findFirst<any>(consorcioId));
        return consorcio ? { ...consorcio, uid: consorcioId } : undefined;
    }

    // consorcistas del mismo piso y departamento (la torre se filtra después, para no depender de índices)
    async getUnitConsorcistas(consorcioId: string, floor: string, apartment: string): Promise<Consorcista[]> {
        this.crudService.setModel('consorcios/' + consorcioId + '/consorcistas');
        const list = await firstValueFrom(this.crudService.findWhere<any>([
            { field: 'floor', operator: '==', value: floor },
            { field: 'apartment', operator: '==', value: apartment },
        ] as any, []));
        return (list ?? []).map((c: any) => ({ ...c, consorcioId }));
    }

    async getCocheras(consorcioId: string, consorcistaId: string): Promise<CocheraConsorcista[]> {
        this.crudService.setModel('consorcios/' + consorcioId + '/consorcistas/' + consorcistaId + '/cocheras');
        return (await firstValueFrom(this.crudService.findAll<any>())) ?? [];
    }

    getKeyRequestsPath(consorcioId: string): string {
        return 'consorcios/' + consorcioId + '/' + KEY_REQUESTS_SUBCOLLECTION;
    }

    newKeyRequestId(consorcioId: string): string {
        return this.firebaseService.newDocId(this.getKeyRequestsPath(consorcioId));
    }

    // todo o nada: el pedido, los datos que se completan y el token usado van en una sola escritura
    commit(writes: { path: string, data: any, mode: 'set' | 'update' }[]): Promise<void> {
        return this.firebaseService.commitWrites(writes);
    }

    /* ---------- pantalla de la administración ---------- */

    // todos los pedidos de la administración, del más nuevo al más viejo (collectionGroup como los reclamos: necesita índice)
    async getAllByAdmin(adminId: string): Promise<KeyRequest[]> {
        this.crudService.setModel(KEY_REQUESTS_SUBCOLLECTION);
        const list = await firstValueFrom(this.crudService.searchCollectionGroupMany<any>([
            { field: 'administrationUserId', operator: '==', value: adminId },
        ], [{ orderBy: 'createdAt', orderByDirection: 'desc' }]));
        return (list ?? []).map(withDates);
    }

    countPending(adminId: string): Promise<number> {
        this.crudService.setModel(KEY_REQUESTS_SUBCOLLECTION);
        return this.crudService.findWhereGroupCount([
            { field: 'administrationUserId', operator: '==', value: adminId },
            { field: 'status', operator: '==', value: 'pending' },
        ], []);
    }

    async getKeyRequest(consorcioId: string, id: string): Promise<KeyRequest | undefined> {
        this.crudService.setModel(this.getKeyRequestsPath(consorcioId));
        const request = await firstValueFrom(this.crudService.findFirst<any>(id));
        return request ? withDates({ ...request, uid: id }) : undefined;
    }

    updateKeyRequest(consorcioId: string, id: string, data: Partial<KeyRequest>): Promise<void> {
        this.crudService.setModel(this.getKeyRequestsPath(consorcioId));
        return this.crudService.update(id, data);
    }

    /* ---------- solo para el generador de links de prueba (ver KeyRequestsManagerService.createTestToken) ---------- */

    async getAdministrationConsorcioIds(administrationId: string): Promise<string[]> {
        this.crudService.setModel('consorcios');
        const list = await firstValueFrom(this.crudService.findWhere<any>([
            { field: 'administrationUserId', operator: '==', value: administrationId },
        ] as any, []));
        return (list ?? []).map((c: any) => c.uid);
    }

    // ids de los consorcistas con ese número en un consorcio (se recorre consorcio por consorcio: un collectionGroup
    // no dice a qué consorcio pertenece cada documento y no todos guardan consorcioId)
    async getConsorcistaIdsByWspId(consorcioId: string, wspId: string): Promise<string[]> {
        this.crudService.setModel('consorcios/' + consorcioId + '/consorcistas');
        const list = await firstValueFrom(this.crudService.findWhere<any>([
            { field: 'wspId', operator: '==', value: wspId },
        ] as any, []));
        return (list ?? []).map((c: any) => c.uid);
    }

    async getActiveTokens(wspId: string): Promise<KeyRequestToken[]> {
        this.crudService.setModel(KEY_REQUEST_TOKENS);
        return (await firstValueFrom(this.crudService.findWhere<any>([
            { field: 'wspId', operator: '==', value: wspId },
            { field: 'status', operator: '==', value: 'active' },
        ] as any, []))) ?? [];
    }
}

function withDates(request: any): KeyRequest {
    return { ...request, createdAt: toDate(request.createdAt), resolvedAt: request.resolvedAt ? toDate(request.resolvedAt) : undefined };
}

export function toDate(value: any): Date {
    if (!value) {
        return new Date(0);
    }
    return value instanceof Date ? value : (value.toDate ? value.toDate() : new Date(value));
}
